import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RevenueVsBudgetChart, EbitdaMarginChart } from "@/components/Charts";
import NewsWidget from "@/components/NewsWidget";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

export default function CountryView() {
    const { countryCode } = useParams();
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    useEffect(() => {
        setData(null); setDenied(false);
        client.get(`/dashboard/country/${countryCode}`).then((r) => setData(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
    }, [countryCode]);

    if (denied) return <AccessDenied />;
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;
    const s = data.snapshot;
    const c = data.consolidated;

    return (
        <div>
            <PageHeader
                overline={<Link to="/" className="hover:text-fp-ink inline-flex items-center gap-1"><ArrowLeft size={11} /> Group Overview</Link>}
                title={data.scope_label}
                description={`${c.entity_count} entities across ${new Set(data.entity_breakdown.map(e => e.sector_id)).size} sectors`}
                period={s.period_label}
            />

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
                    <KpiCard label="Revenue" value={fmtMoney(s.revenue.value_musd, { decimals: 0 })} subValue={`vs Budget: ${fmtPctPlain(s.revenue.budget_variance_pct)}`} delta={s.revenue.yoy_pct} deltaLabel="YoY" trend={s.revenue.trend} testId="country-kpi-revenue" variant="primary" />
                    <KpiCard label="EBITDA" value={fmtMoney(s.ebitda.value_musd, { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(s.ebitda.margin_pct)}`} delta={s.ebitda.mom_pct} deltaLabel="MoM" trend={s.ebitda.trend} variant="success" />
                    <KpiCard label="Free Cash Flow" value={fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 })} trend={s.free_cash_flow.trend} variant="teal" />
                    <KpiCard label="ROIC" value={fmtPctPlain(s.roic_pct)} subValue={`ROE: ${fmtPctPlain(s.roe_pct)}`} variant="plum" />
                    <KpiCard label="Headcount" value={fmtNum(s.headcount)} subValue={`Attrition: ${fmtPctPlain(s.attrition_pct)}`} variant="warn" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-4">
                        <AIInsightsPanel context={`country:${countryCode}`} contextLabel={data.scope_label} />
                    </div>
                    <div className="lg:col-span-5 bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Country Revenue</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Actual · Budget · Prior Year</h3>
                        </div>
                        <RevenueVsBudgetChart periods={data.periods} revenue={c.pnl.revenue} budget={c.pnl.budget_revenue} priorYear={c.pnl.prior_year_revenue} />
                    </div>
                    <div className="lg:col-span-3">
                        <NewsWidget scope={`country:${countryCode}`} />
                    </div>
                </div>

                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">EBITDA Trajectory</div>
                        <h3 className="font-serif text-2xl text-fp-ink">Profit & Margin Evolution</h3>
                    </div>
                    <EbitdaMarginChart periods={data.periods} ebitda={c.pnl.ebitda} marginPct={c.pnl.ebitda_margin_pct} />
                </div>

                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="country-entities">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Entities in {data.scope_label}</div>
                        <h3 className="font-serif text-2xl text-fp-ink">Business Units</h3>
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-fp-ink">
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Entity</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Sector</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Revenue</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">EBITDA</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Margin</th>
                                <th className="pb-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.entity_breakdown.map((e) => (
                                <tr key={e.entity_id} className="border-b border-fp-border hover:bg-fp-bg transition-colors">
                                    <td className="py-3 font-sans text-sm text-fp-ink">{e.entity_name}</td>
                                    <td className="py-3 font-sans text-xs text-fp-secondary capitalize">{e.sector_id}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(e.revenue_musd)}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(e.ebitda_musd)}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-secondary">{fmtPctPlain(e.ebitda_margin_pct)}</td>
                                    <td className="py-3 text-right">
                                        <Link to={`/entity/${e.entity_id}`} className="text-fp-secondary hover:text-fp-ink"><ArrowRight size={14} /></Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
